/**
 * Deployment settings the SPA needs at runtime. Each environment's stack
 * writes `/config.json` into its site bucket at deploy time (API URL, Cognito
 * pool, client and domain), so one build serves every environment and a new
 * environment's generated client id never has to be baked into a build.
 * Locally there is no config.json, and the `VITE_*` variables apply.
 */
export interface RuntimeConfig {
  apiUrl?: string;
  cognitoUserPoolId?: string;
  cognitoClientId?: string;
  cognitoDomain?: string;
}

let loaded: RuntimeConfig = {};

/** Fetches /config.json once, before the app renders. Missing is fine. */
export async function loadRuntimeConfig(): Promise<void> {
  loaded = {};
  try {
    const res = await fetch("/config.json", { cache: "no-store" });
    // The Vite dev server answers unknown paths with index.html.
    const isJson = res.headers.get("content-type")?.includes("json");
    if (res.ok && isJson) loaded = (await res.json()) as RuntimeConfig;
  } catch {
    // No config.json: the build-time variables apply.
  }
}

export function runtimeConfig(): Required<Pick<RuntimeConfig, "apiUrl">> &
  RuntimeConfig {
  return {
    apiUrl:
      loaded.apiUrl ??
      import.meta.env.VITE_CRM_API_URL ??
      "http://127.0.0.1:8787",
    cognitoUserPoolId:
      loaded.cognitoUserPoolId ?? import.meta.env.VITE_COGNITO_USER_POOL_ID,
    cognitoClientId:
      loaded.cognitoClientId ?? import.meta.env.VITE_COGNITO_CLIENT_ID,
    cognitoDomain: loaded.cognitoDomain ?? import.meta.env.VITE_COGNITO_DOMAIN,
  };
}
